const MAX_SIDE = 2000;

export interface PreparedPhoto {
  blob: Blob;
  width: number;
  height: number;
}

/**
 * Scale a phone photo to 2000px on the long side and re-encode it as JPEG,
 * upright, so a 6 MB photo uploads as about 500 KB. Also turns HEIC into JPEG
 * where the browser can decode it.
 */
export async function preparePhoto(file: Blob): Promise<PreparedPhoto> {
  // 'from-image' is valid; TypeScript 4.9's DOM types predate it
  const upright = { imageOrientation: 'from-image' } as unknown as ImageBitmapOptions;
  const bmp = await createImageBitmap(file, upright).catch(() => createImageBitmap(file));
  const scale = Math.min(1, MAX_SIDE / Math.max(bmp.width, bmp.height));
  const width = Math.round(bmp.width * scale);
  const height = Math.round(bmp.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No canvas');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bmp, 0, 0, width, height);
  bmp.close?.();
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
  if (!blob) throw new Error("Couldn't encode the photo");
  return { blob, width, height };
}
